import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserGroupIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  BanknotesIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  ComputerDesktopIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  DocumentCheckIcon,
  UserPlusIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const OnboardingAutomation = () => {
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      id: 1,
      title: "Candidate Interview Stage (ATS)",
      system: "ATS (Zoho Recruit, SAP SF Recruiting, Greenhouse, etc.)",
      icon: UserGroupIcon,
      color: "bg-blue-500",
      trigger: "Candidate status = Selected",
      automatedSteps: [
        "Candidate profile created",
        "Interview rounds scheduled automatically",
        "Interview feedback captured in ATS",
        "Hiring manager decision updated"
      ]
    },
    {
      id: 2,
      title: "Offer Management (ATS)",
      system: "ATS System",
      icon: DocumentTextIcon,
      color: "bg-purple-500",
      trigger: "Offer = Accepted",
      automatedSteps: [
        "Offer letter auto-generated from template",
        "Approval workflow (HR → Finance → Leadership)",
        "Offer email sent to candidate",
        "Candidate e-signs offer letter",
        "Offer acceptance captured"
      ]
    },
    {
      id: 3,
      title: "ATS → HRMS Auto-Conversion",
      system: "Integration Layer",
      icon: ArrowRightIcon,
      color: "bg-emerald-500",
      trigger: "Key Automation Point 🔑",
      isKey: true,
      automatedSteps: [
        "Candidate record auto-converted to Employee",
        "Data sync: Name, Email, Phone",
        "Role, Department, Location sync",
        "Salary details transferred",
        "Joining date set",
        "Employee ID auto-generated",
        "📌 No duplicate data entry"
      ]
    },
    {
      id: 4,
      title: "Pre-Onboarding (HRMS – Before Day 1)",
      system: "HRMS System",
      icon: CalendarDaysIcon,
      color: "bg-orange-500",
      trigger: "Joining date – T-7 / T-5 days",
      automatedSteps: [
        "Welcome email with HRMS login",
        "Pre-joining form triggered",
        "Document upload requests: ID, Address proof, PAN/Aadhaar, Bank details",
        "Background verification auto-initiated",
        "IT & Admin tasks auto-assigned: Laptop, Email ID, Tool access"
      ]
    },
    {
      id: 5,
      title: "Day-1 Onboarding (HRMS)",
      system: "HRMS System",
      icon: UserPlusIcon,
      color: "bg-green-500",
      trigger: "Joining Date",
      automatedSteps: [
        "Employee status → Active",
        "Attendance & shift auto-assigned",
        "Policy documents pushed for e-sign",
        "Manager & team mapped automatically",
        "Asset allocation checklist activated"
      ]
    },
    {
      id: 6,
      title: "Payroll & Compliance Automation",
      system: "HRMS Payroll Module",
      icon: BanknotesIcon,
      color: "bg-yellow-500",
      trigger: "Employee Activation",
      automatedSteps: [
        "Salary structure auto-applied",
        "Statutory rules auto-mapped: PF/ESI/PT/IT",
        "Employee added to payroll cycle",
        "Tax declaration workflow enabled"
      ]
    },
    {
      id: 7,
      title: "Induction & Training Automation",
      system: "HRMS / LMS",
      icon: AcademicCapIcon,
      color: "bg-indigo-500",
      trigger: "Day-1 Completion",
      automatedSteps: [
        "Induction plan auto-assigned",
        "Mandatory trainings triggered",
        "Completion tracking enabled",
        "Alerts sent for pending training"
      ]
    },
    {
      id: 8,
      title: "Probation & Confirmation Automation",
      system: "HRMS Lifecycle",
      icon: ShieldCheckIcon,
      color: "bg-red-500",
      trigger: "Probation Period",
      automatedSteps: [
        "Probation period auto-set",
        "30-60-90 day review reminders",
        "Manager feedback workflow",
        "Confirmation/extension update",
        "Status change reflected automatically"
      ]
    }
  ];

  const StepCard = ({ step, isActive, onClick }) => {
    const IconComponent = step.icon;
    
    return (
      <motion.div
        onClick={onClick}
        className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
          isActive 
            ? 'border-emerald-500 bg-emerald-50 shadow-lg' 
            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className={`${step.color} p-2 rounded-lg`}>
            <IconComponent className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm text-slate-800">{step.title}</h3>
            <p className="text-xs text-slate-500">{step.system}</p>
          </div>
          {step.isKey && (
            <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold">
              KEY
            </div>
          )}
        </div>
        
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <span className="text-xs font-semibold text-slate-600">Trigger:</span>
          </div>
          <p className="text-xs text-slate-700 ml-4">{step.trigger}</p>
        </div>

        <div className="space-y-1">
          {step.automatedSteps.slice(0, 3).map((stepItem, index) => (
            <div key={index} className="flex items-start gap-2">
              <CheckCircleIcon className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-slate-600">{stepItem}</span>
            </div>
          ))}
          {step.automatedSteps.length > 3 && (
            <p className="text-xs text-slate-400 ml-5">+{step.automatedSteps.length - 3} more...</p>
          )}
        </div>
      </motion.div>
    );
  };

  const DetailView = ({ step }) => {
    const IconComponent = step.icon;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-white rounded-lg border border-slate-200 p-6"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className={`${step.color} p-3 rounded-xl`}>
            <IconComponent className="h-8 w-8 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-slate-800">{step.title}</h2>
            <p className="text-slate-600">{step.system}</p>
          </div>
          {step.isKey && (
            <div className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg font-bold">
              🔑 KEY AUTOMATION POINT
            </div>
          )}
        </div>

        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            <span className="font-semibold text-slate-700">Automation Trigger:</span>
          </div>
          <p className="text-slate-800 font-medium ml-5">{step.trigger}</p>
        </div>

        <div>
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <CogIcon className="h-5 w-5 text-emerald-500" />
            Automated Steps:
          </h3>
          <div className="grid gap-3">
            {step.automatedSteps.map((stepItem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100"
              >
                <div className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <span className="text-slate-700">{stepItem}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-500 p-2 rounded-lg">
            <ArrowRightIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Interview → Onboarding Automation</h1>
            <p className="text-slate-600">ATS to HRMS Integration Workflow</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Process Flow Overview */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-emerald-500" />
            Automation Process Flow
          </h2>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div 
                  className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                    activeStep === step.id 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  onClick={() => setActiveStep(step.id)}
                >
                  {step.id}. {step.title.split(' ')[0]}
                </div>
                {index < steps.length - 1 && (
                  <ArrowRightIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Steps Grid */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Automation Steps</h2>
            <div className="space-y-4">
              {steps.map((step) => (
                <StepCard
                  key={step.id}
                  step={step}
                  isActive={activeStep === step.id}
                  onClick={() => setActiveStep(step.id)}
                />
              ))}
            </div>
          </div>

          {/* Detail View */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Step Details</h2>
            <AnimatePresence mode="wait">
              <DetailView key={activeStep} step={steps.find(s => s.id === activeStep)} />
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingAutomation;